export function leadProseOf(body: string): string {
  const withoutTitle = body.replace(/^#[^\n#][^\n]*\n?/, "");
  const heading = /^#{1,6}\s/m.exec(withoutTitle);
  return (heading ? withoutTitle.slice(0, heading.index) : withoutTitle).trim();
}
const rulesPageTiers: RegExp[] = [
  /^rules?\b|rules-summary|game-rules/,
  /scoring|glory|elo/,
  /mechanic|how-to-play|game-guide/,
];
function normalizeForMatch(text: string): string {
  return text
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-");
}
export function pickRulesPage<
  T extends {
    slug: string;
    title: string;
  },
>(pages: readonly T[]): T | null {
  for (const tier of rulesPageTiers) {
    const hit = pages.find(
      (p) =>
        tier.test(normalizeForMatch(p.title)) ||
        tier.test(normalizeForMatch(p.slug)),
    );

    if (hit) return hit;
  }

  return null;
}
export const rulesHeadingPattern = /rules|scoring/i;
const headingLinePattern = /^#{1,6}\s+(.+)$/gm;
export function proseUnderHeading({
  body,
  match,
}: {
  body: string;
  match: RegExp;
}): {
  heading: string;
  body: string;
} | null {
  const headings = [...body.matchAll(headingLinePattern)].map((hit) => ({
    heading: hit[0].replace(/^#{1,6}\s+/, ""),
    start: hit.index + hit[0].length,
    index: hit.index,
  }));

  for (const [index, hit] of headings.entries()) {
    if (!match.test(hit.heading)) continue;

    const end = headings[index + 1]?.index ?? body.length;
    const prose = body.slice(hit.start, end).trim();
    return prose ? { heading: hit.heading.trim(), body: prose } : null;
  }

  return null;
}
const selfDeprecatedLeadPattern = /^>.*\bdeprecated\b/i;
export function isSelfDeclaredDeprecated(text: string): boolean {
  return selfDeprecatedLeadPattern.test(text.trim().split("\n")[0] ?? "");
}
const developerDocPatterns: readonly RegExp[] = [
  /\b[\w.-]+\/[\w./-]+\.(?:md|py|ts|json|yaml|yml)\b/,
  /fork of/i,
  /protocol/i,
  /websocket/i,
  /endpoint/i,
  /docker/i,
  /PR #/i,
  /clone /i,
  /pip install/i,
  /uv run/i,
  /repo/i,
  /README/i,
  /synced snapshot/i,
  /\bsource site\b/i,
  /\bhost surface\b/i,
];
export function looksLikeDeveloperDoc(body: string): boolean {
  return developerDocPatterns.some((re) => re.test(body));
}
const githubBlobPattern =
  /^https:\/\/github\.com\/([^/]+)\/([^/]+)\/blob\/([^/]+)\/(.+)$/;
export function githubRawUrl(url: string): string | null {
  const m = githubBlobPattern.exec(url);
  return m
    ? `https://raw.githubusercontent.com/${m[1]}/${m[2]}/${m[3]}/${m[4]}`
    : null;
}
const htmlSniffPattern = /^\s*<(!doctype|html)\b/i;
export function looksLikeHtml(text: string): boolean {
  return htmlSniffPattern.test(text);
}
const h1Pattern = /^#[ \t]+(.+?)[ \t]*$/m;
export function h1Of(body: string): string | null {
  const m = h1Pattern.exec(body);
  return m ? m[0].replace(h1Pattern, "$1").trim() : null;
}
const h2Pattern = /^##[ \t]+.+$/gm;
const externalRulesLeadMaxChars = 1200;
export function leadOfExternalRules(body: string): string {
  const withoutTitle = body.replace(/^#[^\n#][^\n]*\n?/, "");
  const h2s = [...withoutTitle.matchAll(h2Pattern)];
  const secondHeading = h2s[1];
  const uptoSecondH2 = secondHeading
    ? withoutTitle.slice(0, secondHeading.index)
    : withoutTitle;
  let capped = withoutTitle;

  if (withoutTitle.length > externalRulesLeadMaxChars) {
    const truncated = withoutTitle.slice(0, externalRulesLeadMaxChars);
    const lastParagraph = truncated.lastIndexOf("\n\n");
    capped = lastParagraph > 0 ? truncated.slice(0, lastParagraph) : truncated;
  }

  return (uptoSecondH2.length <= capped.length ? uptoSecondH2 : capped).trim();
}
